/**
 * Cart Backorder Calculator
 * Calculates delivery dates based on backend stock data
 * Renders backorder messages for customers
 */

const _backorderLog =
  typeof window.createLogger === 'function'
    ? window.createLogger('CartBackorderCalculator')
    : { log: () => {}, warn: () => {}, error: () => {} };

class CartBackorderCalculator {
  constructor() {
    this.containerSelector = '#backorder-message-container';
    this.apiClient = window.externalApiClient;
    // Shipping transit days provided by shipping selector events (EU / UK)
    // Represented as object { min: number, max: number }
    this.currentShippingDays = { min: 0, max: 0 };

    _backorderLog.log('Constructor called');
    _backorderLog.log('API client available:', !!this.apiClient);
    if (!this.apiClient) {
      _backorderLog.error(
        '[CartBackorderCalculator] externalApiClient not found — this should not happen after init guard.'
      );
      return;
    }

    this.init();
  }

  /**
   * Initialize calculator
   */
  init() {
    // DOM is guaranteed to be ready when this is called
    this.calculate();

    // Listen for shipping selection events (EU and UK markets)
    // Expectation: event.detail may include a shipping days value under one of several keys.
    window.addEventListener('EU-shipping-rate-selected', (event) => {
      try {
        const range = this._parseShippingDays(event.detail || {});
        _backorderLog.log('EU shipping selected, range:', range);
        this.currentShippingDays = range;

        // Recalculate estimates with new shipping days
        this.calculate();
      } catch (err) {
        _backorderLog.warn('Failed to handle EU shipping event', err);
      }
    });

    window.addEventListener('UK-shipping-rate-selected', (event) => {
      try {
        const range = this._parseShippingDays(event.detail || {});
        _backorderLog.log('UK shipping selected, range:', range);
        this.currentShippingDays = range;

        // Recalculate estimates with new shipping days
        this.calculate();
      } catch (err) {
        _backorderLog.warn('Failed to handle UK shipping event', err);
      }
    });

    // Listen for custom cart processed event from forklift
    window.addEventListener('cart-forklift-processed', (event) => {
      try {
        _backorderLog.log('Cart processed by forklift, recalculating delivery dates');
        // Recalculate immediately with fresh cart data via fetchCart()
        this.calculate();
      } catch (err) {
        _backorderLog.warn('Cart forklift processed event error:', err);
      }
    });
  }

  /**
   * Extract SKUs from Shopify cart API response (API-based - for dynamic updates)
   * @param {Object} cartData - Shopify cart.js API response
   * @returns {Array} - Array of { sku, quantity, productTitle, variantTitle }
   */
  extractCartItemsFromAPI(cartData) {
    const items = [];

    if (!cartData || !cartData.items || !Array.isArray(cartData.items)) {
      _backorderLog.warn('Invalid cart data structure:', cartData);
      return items;
    }

    cartData.items.forEach((item) => {
      const sku = item.sku;
      const quantity = parseInt(item.quantity, 10) || 0;
      const productTitle = item.product_title || 'Unknown Product';
      const variantTitle = item.variant_title || 'Default Title';

      _backorderLog.log('Extracted cart item from API:', productTitle, sku, 'qty:', quantity);

      // Skip items without SKU
      if (sku && sku.trim()) {
        items.push({
          sku: sku.trim(),
          quantity,
          productTitle,
          variantTitle
        });
      }
    });

    _backorderLog.log('Extracted cart items from API:', items);
    return items;
  }

  /**
   * Calculate delivery dates for backorder items
   * @param {Array} cartItems - Cart items from extractCartItemsFromAPI()
   * @param {Map} stockData - Stock data from API
   * @returns {Object} - { hasBackorder, backorderItems, latestDeliveryTimestamp }
   */
  calculateDeliveryDates(cartItems, stockData) {
    _backorderLog.log('calculateDeliveryDates() called');
    _backorderLog.log('cartItems:', cartItems);
    _backorderLog.log('stockData:', stockData);

    let hasBackorder = false;
    const backorderItems = [];
    const inStockItems = [];
    let latestDeliveryTimestamp = 0;
    let globalShippingMin = 0;
    let globalShippingMax = 0;
    let maxProductionDays = 0;

    cartItems.forEach((item) => {
      const stock = stockData.get(item.sku);

      _backorderLog.log(`Processing item SKU: ${item.sku}, stock:`, stock);

      if (!stock) {
        _backorderLog.warn(`No stock data found for SKU: ${item.sku}`);
        return;
      }

      const inStockQty = parseInt(stock.in_stock_quantity, 10) || 0;
      const pendingOrderQty = parseInt(stock.pending_ordered_quantity, 10) || 0;
      const orderedQty = item.quantity;

      // Available quantity for new orders = in_stock - pending_orders
      const availableQty = Math.max(inStockQty - pendingOrderQty, 0);

      _backorderLog.log(
        `inStockQty: ${inStockQty}, pendingOrderQty: ${pendingOrderQty}, availableQty: ${availableQty}, orderedQty: ${orderedQty}`
      );

      // Determine shipping min/max days (from last selected shipping option) - needed for both backorder and in-stock items
      const shippingMin = Number(this.currentShippingDays && this.currentShippingDays.min) || 0;
      const shippingMax = Number(this.currentShippingDays && this.currentShippingDays.max) || 0;

      // Track global shipping range for final message
      if (shippingMin > 0 || shippingMax > 0) {
        if (globalShippingMin === 0) globalShippingMin = shippingMin;
        if (globalShippingMax === 0) globalShippingMax = shippingMax;
      }

      // Check if ordered quantity exceeds available quantity (after considering pending orders)
      if (orderedQty > availableQty) {
        // Extract all data from API response (no config object needed)
        const leadTimeDays = parseInt(stock.lead_time_in_days, 10) || 0;
        const dailyCapacity = Math.max(parseInt(stock.daily_production_capacity, 10) || 1, 1);
        const manufacturingTypeId = stock.manufacturing_type_id;
        const manufacturingTypeName = stock.manufacturing_type_name || '';

        // Determine if product is manufactured (manufacturing_type_id === 1 or name includes 'manufactur')
        const isManufactured = manufacturingTypeId === 1 || manufacturingTypeName === 'Manufactured';

        const remaining = orderedQty - availableQty;
        let manufacturingDays = 0;

        if (isManufactured) {
          // Calculate manufacturing days needed
          manufacturingDays = Math.ceil(remaining / dailyCapacity);
          if (manufacturingDays < 1) manufacturingDays = 1;
        }

        // productionDays includes lead time + manufacturing time (if any)
        const productionDays = leadTimeDays + manufacturingDays;

        // Track maximum production days across all backorder items for combined estimate
        if (productionDays > maxProductionDays) {
          maxProductionDays = productionDays;
        }

        _backorderLog.log(
          `API stock data - leadTimeDays: ${leadTimeDays}, manufacturingDays: ${manufacturingDays}, isManufactured: ${isManufactured}`
        );

        // Always include both production time and shipping time in the delivery estimate using working days only.
        // Total days = production days + shipping days, all counted as working days from today.
        const nowDate = new Date();
        const totalDaysForEarliest = Math.max(0, productionDays + shippingMin);
        const totalDaysForLatest = Math.max(0, productionDays + (shippingMax || shippingMin));

        _backorderLog.log(
          `Delivery calc - productionDays: ${productionDays}, shippingMin: ${shippingMin}, shippingMax: ${shippingMax}, totalDaysForLatest: ${totalDaysForLatest}`
        );

        const earliestDeliveryDate = this.addWorkingDays(nowDate, totalDaysForEarliest);
        const latestDeliveryDate = this.addWorkingDays(nowDate, totalDaysForLatest);
        const deliveryTimestamp = Math.floor(latestDeliveryDate.getTime() / 1000);

        _backorderLog.log(
          `Delivery dates - earliest: ${this.formatDate(Math.floor(earliestDeliveryDate.getTime() / 1000))}, latest: ${this.formatDate(Math.floor(latestDeliveryDate.getTime() / 1000))}`
        );

        hasBackorder = true;

        backorderItems.push({
          productTitle: item.productTitle,
          variantTitle: item.variantTitle,
          availableQty,
          remaining,
          productionDays,
          earliestDeliveryDate: this.formatDate(Math.floor(earliestDeliveryDate.getTime() / 1000)),
          latestDeliveryDate: this.formatDate(Math.floor(latestDeliveryDate.getTime() / 1000)),
          deliveryTimestamp,
          deliveryDate: this.formatDate(deliveryTimestamp)
        });

        _backorderLog.log(
          `Backorder item: ${item.sku} - ${remaining} items, productionDays: ${productionDays}, manufacturingDays: ${manufacturingDays}`
        );
      } else {
        // Item is fully in stock - track for shipping-only delivery estimate
        inStockItems.push({
          productTitle: item.productTitle,
          variantTitle: item.variantTitle,
          quantity: orderedQty
        });
        _backorderLog.log(`In-stock item: ${item.sku} - ${orderedQty} items available`);
      }
    });

    // Combined delivery estimate for backorders: use the longest production time across items
    let combinedEarliestDate = null;
    let combinedLatestDate = null;
    if (hasBackorder) {
      const nowDate = new Date();
      const totalEarliestDays = Math.max(0, maxProductionDays + globalShippingMin);
      const totalLatestDays = Math.max(0, maxProductionDays + (globalShippingMax || globalShippingMin));
      const earliestDate = this.addWorkingDays(nowDate, totalEarliestDays);
      const latestDate = this.addWorkingDays(nowDate, totalLatestDays);
      latestDeliveryTimestamp = Math.floor(latestDate.getTime() / 1000);
      combinedEarliestDate = this.formatDate(Math.floor(earliestDate.getTime() / 1000));
      combinedLatestDate = this.formatDate(latestDeliveryTimestamp);
      _backorderLog.log('Combined delivery using max production days:', {
        maxProductionDays,
        globalShippingMin,
        globalShippingMax,
        combinedEarliestDate,
        combinedLatestDate
      });
    }

    // Calculate shipping/delivery dates for in-stock items (with or without shipping selection)
    // ALWAYS calculate dates for in-stock items to support messaging even when shipping not selected
    let inStockEarliestDate = null;
    let inStockLatestDate = null;
    if (!hasBackorder && inStockItems.length > 0) {
      const nowDate = new Date();
      const shippingMin = globalShippingMin || 0;
      const shippingMax = globalShippingMax || 0;

      // If shipping is selected, calculate delivery dates; otherwise use production date (ready to ship)
      const daysForEarliest = shippingMin;
      const daysForLatest = shippingMax || shippingMin;

      inStockEarliestDate = this.formatDate(Math.floor(this.addWorkingDays(nowDate, daysForEarliest).getTime() / 1000));
      inStockLatestDate = this.formatDate(Math.floor(this.addWorkingDays(nowDate, daysForLatest).getTime() / 1000));
    }

    _backorderLog.log('Final backorderData:', {
      hasBackorder,
      backorderItems,
      latestDeliveryTimestamp,
      globalShippingMin,
      globalShippingMax,
      maxProductionDays,
      combinedEarliestDate,
      combinedLatestDate
    });

    return {
      hasBackorder,
      backorderItems,
      inStockItems,
      latestDeliveryTimestamp,
      latestDeliveryDate: latestDeliveryTimestamp > 0 ? this.formatDate(latestDeliveryTimestamp) : null,
      combinedEarliestDate,
      combinedLatestDate,
      inStockEarliestDate,
      inStockLatestDate,
      // shippingRange remains the raw transit window; delivery dates already include production + shipping
      shippingRange: { min: globalShippingMin, max: globalShippingMax }
    };
  }

  /**
   * Format timestamp to readable date string
   * @param {number} timestamp - Unix timestamp (seconds)
   * @returns {string} - Formatted date (e.g., "Mon, Nov 24, 2025")
   */
  formatDate(timestamp) {
    const date = new Date(timestamp * 1000);
    const options = { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  }

  /**
   * Render backorder message HTML
   * Intelligently displays:
   * - In-stock items: shipping/delivery date (with or without shipping selected)
   * - Backorder items: production + shipping estimate with details
   * @param {Object} backorderData - Data from calculateDeliveryDates()
   * @returns {string} - HTML string
   */
  renderBackorderMessage(backorderData, cartItems) {
    // Check if shipping is selected (shipping range has values)
    const hasShippingSelected =
      backorderData.shippingRange && (backorderData.shippingRange.min > 0 || backorderData.shippingRange.max > 0);

    // CASE 1: No backorder (all items in stock)
    // Show shipping/delivery date message for in-stock items (with or without shipping selected)
    if (
      !backorderData.hasBackorder &&
      backorderData.inStockItems &&
      backorderData.inStockItems.length > 0 &&
      backorderData.inStockEarliestDate
    ) {
      return this._renderInStockMessage(backorderData, hasShippingSelected);
    }

    // CASE 2: Has backorder items
    if (!backorderData.hasBackorder) {
      return '';
    }

    // If only one item in cart, simplify message by omitting list

    if (cartItems.length === 1) {
      const orderedQty = cartItems[0].quantity;
      if (orderedQty === 1) {
        return this._renderSingleCartItemBackorderMessage(backorderData, hasShippingSelected);
      }
    }

    // Load translations for item list lines
    const trList =
      window.CartTranslations && typeof window.CartTranslations === 'object' ? window.CartTranslations : {};
    const availableSuffix = trList.available_for_immediate_shipping_suffix || 'available for immediate shipping.';
    const beingMadePrefix = trList.being_made_prefix || 'being made for you and will be ready in';
    const itemIsText = trList.item_is || 'item is';
    const itemsAreText = trList.items_are || 'items are';
    const daySing = trList.day || 'day';
    const dayPlur = trList.days || 'days';

    const itemsHtml = backorderData.backorderItems
      .map((item) => {
        let itemHtml = `
      <li>
        <strong>${this.escapeHtml(item.productTitle)}</strong>`;

        if (item.variantTitle && item.variantTitle !== 'Default Title') {
          itemHtml += `
        <strong> (${this.escapeHtml(item.variantTitle)})</strong>`;
        }

        itemHtml += `<br>`;

        if (item.availableQty > 0) {
          const itemText = item.availableQty === 1 ? itemIsText : itemsAreText;
          itemHtml += `• ${item.availableQty} ${itemText} ${availableSuffix}<br>`;
        }

        if (item.remaining > 0) {
          const itemText = item.remaining === 1 ? itemIsText : itemsAreText;
          const dayText = item.productionDays === 1 ? daySing : dayPlur;
          itemHtml += `• ${item.remaining} ${itemText} ${beingMadePrefix} ${item.productionDays} ${dayText}.<br>`;
        }

        itemHtml += `</li>`;

        return itemHtml;
      })
      .join('');

    // Localize header and explanatory lines
    const tr = window.CartTranslations && typeof window.CartTranslations === 'object' ? window.CartTranslations : {};
    const noteHeading = tr.note_heading || 'Note:';
    const backorderExplanation =
      tr.backorder_explanatory_line || 'Your order includes items that require additional preparation time.';
    const shippedTogether =
      tr.shipped_together_line || 'All items will be <strong>shipped together once your full order is ready.</strong>';

    let html = `
    <div
      class='cart-shipping-note'
      style='
        padding-top: 1em;
        padding-bottom: 1em;
        background:#ffffff;
        padding-left: 1em;
        padding-right: 1em;
        border-left: 1px solid #e5e7eb;
        border-right: 1px solid #e5e7eb;
        border-top: 1px solid #e5e7eb;
        border-bottom: 1px solid #e5e7eb;
        border-radius: 12px 12px 12px 12px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.05);
      '
    >
      <strong>${noteHeading}</strong> ${backorderExplanation}<br>
      <strong>${shippedTogether}</strong><br>

      <ul style='margin-top:0.5em; padding-left:1.2em; list-style-type:disc;'>
        ${itemsHtml}
      </ul>`;

    if (backorderData.latestDeliveryTimestamp > 0) {
      html += this._renderDateMessage(backorderData, hasShippingSelected);
    }

    html += `
    </div>`;

    return html;
  }

  _renderSingleCartItemBackorderMessage(backorderData, hasShippingSelected) {
    let html = `
    <div
      class='cart-shipping-note'
      style='
        padding-top: 1em;
        padding-bottom: 1em;
        background:#ffffff;
        padding-left: 1em;
        padding-right: 1em;
        border-left: 1px solid #e5e7eb;
        border-right: 1px solid #e5e7eb;
        border-top: 1px solid #e5e7eb;
        border-bottom: 1px solid #e5e7eb;
        border-radius: 12px 12px 12px 12px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.05);
      '
    >`;

    if (backorderData.latestDeliveryTimestamp > 0) {
      html += this._renderDateMessage(backorderData, hasShippingSelected, true);
    }

    html += `
    </div>`;

    return html;
  }

  /**
   * Render in-stock items message with shipping option
   * For fully stocked carts: shows "Next day shipping" for customer appeal
   * Customer-friendly messaging emphasizing immediate availability
   * @param {Object} backorderData - Backorder data
   * @param {boolean} hasShippingSelected - Whether shipping is selected
   * @returns {string} - HTML string
   * @private
   */
  _renderInStockMessage(backorderData, hasShippingSelected) {
    let messageContent = '';
    // Determine singular/plural based on cart items present in DOM
    const itemEls = document.querySelectorAll('.cart-item, .cart-items .cart-item');
    const itemCount = itemEls ? itemEls.length : 0;

    // Load translations injected server-side into window.CartTranslations (fall back to English)
    const tr = window.CartTranslations && typeof window.CartTranslations === 'object' ? window.CartTranslations : {};
    const inStockText = tr.in_stock_text || 'in stock';
    const readyToShipText = tr.ready_to_ship_text || 'Ready to ship';
    const selectShippingText =
      tr.select_shipping_to_see_delivery || 'Select a shipping option to see your delivery date';
    const estimatedDeliveryPrefix = tr.estimated_delivery_prefix || 'Estimated delivery: ';
    const itemLabel =
      itemCount === 1
        ? tr.item_label_one || (tr.item_label && tr.item_label.one) || 'Cart item'
        : tr.item_label_other || (tr.item_label && tr.item_label.other) || 'Cart items';

    // For in-stock items: all products available for immediate shipment
    if (hasShippingSelected) {
      // Shipping already selected: show confirmed delivery date
      const deliveryDate = backorderData.inStockLatestDate || backorderData.inStockEarliestDate;
      messageContent = `
        <strong>✓ ${itemLabel} ${inStockText}</strong><br>
        <span style='font-size: 0.9em; color: #666;'>${estimatedDeliveryPrefix}<strong>${deliveryDate}</strong></span>`;
    } else {
      // Shipping NOT selected: encourage shipping selection
      const countryCodeCheckout = window.getSelectedMarketCountry() || window.Shopify?.country || 'GB';

      const isEuCountryCheckout = window.is_EuCountry(countryCodeCheckout);
      if (isEuCountryCheckout) {
        messageContent = `
        <strong>✓ ${itemLabel} ${inStockText} - ${readyToShipText}</strong><br>`;
      } else {
        messageContent = `
        <strong>✓ ${itemLabel} ${inStockText} - ${readyToShipText}</strong><br>
        <span style='font-size: 0.9em; color: #666;'>${selectShippingText}</span>`;
      }
    }

    return `
      <div
        class='cart-shipping-note'
        style='
          padding-top: 1em;
          padding-bottom: 1em;
          background:#f0f9f7;
          padding-left: 1em;
          padding-right: 1em;
          border-left: 4px solid #2d7a6b;
          border-right: 1px solid #e5e7eb;
          border-top: 1px solid #e5e7eb;
          border-bottom: 1px solid #e5e7eb;
          border-radius: 8px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        '
      >
        <p style='margin: 0; margin-top: 0.25em;'>
          ${messageContent}
        </p>
      </div>`;
  }

  /**
   * Render date message for backorder items
   * Handles both shipping selected and not selected scenarios
   * @param {Object} backorderData - Backorder data
   * @param {boolean} hasShippingSelected - Whether shipping is selected
   * @returns {string} - HTML string
   * @private
   */
  _renderDateMessage(backorderData, hasShippingSelected, isSingleCartItem = false) {
    // Prefer combined dates (using max production across items)
    const earliestDateCombined = backorderData.combinedEarliestDate || backorderData.latestDeliveryDate;
    const latestDateCombined = backorderData.combinedLatestDate || backorderData.latestDeliveryDate;

    let dateRangeMessage = '';
    let dateLabel = '';
    const trDate =
      window.CartTranslations && typeof window.CartTranslations === 'object' ? window.CartTranslations : {};
    const yourOrderText = trDate.your_order || 'your order';
    const yourCompleteOrderText = trDate.your_complete_order || 'your complete order';
    const completeOrderText = isSingleCartItem ? yourOrderText : yourCompleteOrderText;
    const estimatedDeliveryForTemplate = trDate.estimated_delivery_for || 'Estimated delivery for {what}';
    const readyToShipOnTemplate = trDate.ready_to_ship_on || '{what} will be ready to ship on';

    // Check if we have a shipping range (min and max are different)
    if (
      hasShippingSelected &&
      backorderData.shippingRange &&
      backorderData.shippingRange.min !== backorderData.shippingRange.max &&
      backorderData.shippingRange.min > 0
    ) {
      // Show date range when shipping selected and there's a range
      dateRangeMessage = `${earliestDateCombined} to ${latestDateCombined}`;
      dateLabel = estimatedDeliveryForTemplate.replace('{what}', completeOrderText);
    } else if (hasShippingSelected) {
      // Show single delivery date when shipping selected
      dateRangeMessage = latestDateCombined;
      dateLabel = estimatedDeliveryForTemplate.replace('{what}', completeOrderText);
    } else {
      // Show shipping date when shipping NOT selected (production date only)
      dateRangeMessage = latestDateCombined;
      dateLabel = readyToShipOnTemplate.replace('{what}', completeOrderText);
    }

    return `
      <p style='margin-top:0.5em;'>
        <strong>${dateLabel}: ${dateRangeMessage}.</strong>
      </p>`;
  }

  /**
   * Escape HTML special characters
   * @param {string} text - Text to escape
   * @returns {string} - Escaped text
   */
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Insert rendered HTML into container
   * @param {string} html - HTML to insert
   */
  insertHtml(html) {
    const container = document.querySelector(this.containerSelector);
    if (!container) {
      _backorderLog.warn(`Container not found: ${this.containerSelector}`);
      return;
    }

    container.innerHTML = html;
    _backorderLog.log('HTML inserted into container');
  }

  /**
   * Main calculation flow
   * Always fetches fresh cart data using fetchCart() to ensure single source of truth
   */
  async calculate() {
    try {
      _backorderLog.log('calculate() called - fetching fresh cart data');

      // Always fetch fresh cart data from single source of truth (fetchCart)
      const fetchedCartData = await window.CartUtils.fetchCart();
      _backorderLog.log('Fresh cart data from fetchCart():', JSON.stringify(fetchedCartData, null, 2));

      if (!fetchedCartData || !fetchedCartData.items || fetchedCartData.items.length === 0) {
        _backorderLog.log('No items in fetched cart data');
        this.insertHtml(''); // Clear any existing message
        return;
      }

      // Extract cart items from fetched cart data using API method
      const cartItems = this.extractCartItemsFromAPI(fetchedCartData);

      if (cartItems.length === 0) {
        _backorderLog.log('No cart items with SKU found');
        this.insertHtml(''); // Clear any existing message
        return;
      }

      _backorderLog.log('Complete cart items object:', JSON.stringify(cartItems, null, 2));

      // Fetch stock data from API
      const skuArray = cartItems.map((item) => item.sku);
      const stockData = await this.apiClient.fetchStockData(skuArray);

      if (stockData.size === 0) {
        _backorderLog.log('No stock data returned from API, hiding backorder message');
        this.insertHtml(''); // Clear any existing message
        return;
      }

      // Calculate delivery dates
      // const backorderData = this.calculateDeliveryDates(cartItems, stockData);

      // Render and insert HTML
      // const html = this.renderBackorderMessage(backorderData, cartItems);
      // this.insertHtml(html);

      _backorderLog.log('Calculation completed successfully');
    } catch (error) {
      _backorderLog.error('Error during calculation:', error);
      // Silently fail - no message shown
    }
  }

  /**
   * Parse shipping days into a {min, max} range. Accepts numbers, numeric strings or ranges like "3-5".
   * @param {Object|string|number} detail
   * @returns {{min:number,max:number}}
   */
  _parseShippingDays(detail) {
    // Expecting payload shape with `detail.shipping.days` as a string like "4" or a range "3-5".
    if (!detail) return { min: 0, max: 0 };

    const shipping = detail.shipping || null;
    let daysValue = null;

    if (shipping && shipping.days != null) {
      daysValue = String(shipping.days).trim();
    } else if (detail.days != null) {
      daysValue = String(detail.days).trim();
    }

    if (!daysValue) return { min: 0, max: 0 };

    const rangeMatch = daysValue.match(/^(\d+)\s*-\s*(\d+)$/);
    if (rangeMatch) {
      return { min: Number(rangeMatch[1]), max: Number(rangeMatch[2]) };
    }

    const n = Number(daysValue);
    return { min: isNaN(n) ? 0 : n, max: isNaN(n) ? 0 : n };
  }

  /**
   * Add working days to a Date (skips Saturday and Sunday)
   * @param {Date} date
   * @param {number} workingDays
   * @returns {Date}
   */
  addWorkingDays(date, workingDays) {
    const result = new Date(date);
    if (!workingDays || workingDays <= 0) return result;
    let daysToAdd = Math.floor(workingDays);
    while (daysToAdd > 0) {
      result.setDate(result.getDate() + 1);
      const dow = result.getDay();
      if (dow === 0 || dow === 6) {
        // weekend, skip
        continue;
      }
      daysToAdd -= 1;
    }
    return result;
  }
}

// Initialize CartBackorderCalculator when script loads.
// Retries until window.externalApiClient is available (handles defer load-order edge cases).
const initCartBackorderCalculator = (retries = 20) => {
  if (!window.externalApiClient) {
    if (retries > 0) {
      setTimeout(() => initCartBackorderCalculator(retries - 1), 100);
    } else {
      console.error('[CartBackorderCalculator] externalApiClient never became available after retries.');
    }
    return;
  }
  new CartBackorderCalculator();
};

// Ensure CartBackorderCalculator initializes once when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initCartBackorderCalculator());
} else {
  initCartBackorderCalculator();
}

_backorderLog.log('Initialized');
